import { refreshSessionOnce, TOKEN_STORAGE_KEY } from '@pulse/auth-ui';

import { getAdminEnvironment } from '@/env';

export class ApiRequestError extends Error {
  constructor(
    message: string,
    readonly statusCode: number,
    readonly responseBody: string,
  ) {
    super(message);
    this.name = 'ApiRequestError';
  }

  /** Returns the human-readable message from the API error envelope when present. */
  toUserMessage(): string {
    if (this.responseBody === '') return this.message;
    try {
      const parsed = JSON.parse(this.responseBody) as { message?: string; error?: string };
      if (typeof parsed.message === 'string' && parsed.message.trim().length > 0) return parsed.message;
      if (typeof parsed.error === 'string' && parsed.error.trim().length > 0) return parsed.error;
    } catch {
      // Fall through to default.
    }
    return this.message;
  }
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly searchParams?: Record<string, string | number | undefined>;
  readonly signal?: AbortSignal;
}

export async function apiRequest<TResponse>(path: string, options: RequestOptions = {}): Promise<TResponse> {
  const { method = 'GET', body, searchParams, signal } = options;
  const url = buildUrl(path, searchParams);
  const serializedBody = body === undefined ? undefined : JSON.stringify(body);

  const send = (token: string | null): Promise<Response> => {
    const headers = new Headers({ Accept: 'application/json' });
    if (token !== null && token.length > 0) headers.set('Authorization', `Bearer ${token}`);
    if (serializedBody !== undefined) headers.set('Content-Type', 'application/json');
    return fetch(url, {
      method,
      headers,
      cache: 'no-store',
      ...(serializedBody === undefined ? {} : { body: serializedBody }),
      ...(signal === undefined ? {} : { signal }),
    });
  };

  const sentToken = readBearerToken();
  let response = await send(sentToken);
  // The session token is short-lived. On a 401, renew it once through the auth-ui provider
  // (shared with every other request that 401s at the same time) and retry once. When the
  // refresh fails the provider clears the session and its guard sends the user to /login.
  if (response.status === 401 && sentToken !== null && (await refreshSessionOnce(sentToken))) {
    response = await send(readBearerToken());
  }

  const responseText = await response.text();
  if (!response.ok) {
    throw new ApiRequestError(
      `Request failed with status ${response.status}.`,
      response.status,
      responseText,
    );
  }
  if (responseText === '') return undefined as TResponse;
  try {
    return JSON.parse(responseText) as TResponse;
  } catch {
    throw new ApiRequestError(
      'The server returned data that is not valid JSON.',
      response.status,
      responseText,
    );
  }
}

/**
 * The API authenticates with a bearer token issued by the auth-ui flow and stored in
 * localStorage. Read it on every request so a fresh login, a refresh, or a logout is picked up
 * without restarting the app.
 */
function readBearerToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    // localStorage may be unavailable (e.g. private mode); fall through unauth.
    return null;
  }
}

export function buildUrl(path: string, searchParams: Record<string, string | number | undefined> | undefined, apiUrl = getAdminEnvironment().apiUrl): string {
  // The API URL is a prefix, not just an origin: prod's is API Gateway's stage URL,
  // `https://<id>.execute-api…/prod`. `new URL('/api/…', base)` would replace the base's whole path
  // and drop the stage; API Gateway answers that 403 with no CORS headers, which the browser shows
  // as a "CORS error". Resolving the path relatively against the base with a trailing slash keeps
  // the stage (the same fix as H2H Spark's shared API client). Dev's custom domain has no stage.
  const url = new URL(path.startsWith('/') ? path.slice(1) : path, apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`);
  if (searchParams !== undefined) {
    for (const [key, value] of Object.entries(searchParams)) {
      if (value === undefined) continue;
      url.searchParams.set(key, String(value));
    }
  }
  return url.toString();
}
